import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import {
  LocationSelector,
  LocationSelectionItem,
} from 'components/pricing/components/LocationSelector';
import { AutocompleteUser } from 'components/AutocompleteUser';
import { DatePicker } from 'components/DatePicker';
import { Scope, CustomerClassificationEnum } from '@libs/schemas';
import { Customer } from 'src/trpc';

// Use schema Scope type directly
type FormScope = Scope;

// Use a subset of PricingRule for timing fields
interface ScopeTimingFormData {
  scope: FormScope;
  effectiveFrom: string;
  effectiveTo?: string;
}

interface ErrorRecord {
  [key: string]: string;
}

interface ScopeAndTimingStepProps {
  formData: ScopeTimingFormData;
  errors: ErrorRecord;
  scopeType: string;
  selectedLocations: LocationSelectionItem[];
  customerIds: string[];
  onFormDataChange: (updates: Partial<ScopeTimingFormData>) => void;
  onScopeTypeChange: (scopeType: string) => void;
  onSelectedLocationsChange: (locations: LocationSelectionItem[]) => void;
  onCustomerIdsChange: (customerIds: string[]) => void;
}

const customerClassifications = [
  { value: CustomerClassificationEnum.INDIVIDUAL, label: 'Individual' },
  { value: CustomerClassificationEnum.CONTRACTOR, label: 'Contractor' },
];

export function ScopeAndTimingStep({
  formData,
  errors,
  scopeType,
  selectedLocations,
  customerIds,
  onFormDataChange,
  onScopeTypeChange,
  onSelectedLocationsChange,
  onCustomerIdsChange,
}: ScopeAndTimingStepProps) {
  const [selectedCustomers, setSelectedCustomers] = useState<Customer[]>([]);

  const handleCustomerSelect = (customer: Customer) => {
    const customerId = customer._id!;
    if (!customerIds.includes(customerId)) {
      const newCustomerIds = [...customerIds, customerId];
      const newSelectedCustomers = [...selectedCustomers, customer];
      onCustomerIdsChange(newCustomerIds);
      setSelectedCustomers(newSelectedCustomers);
    }
  };

  const handleRemoveCustomer = (customerId: string) => {
    const newCustomerIds = customerIds.filter((id) => id !== customerId);
    const newSelectedCustomers = selectedCustomers.filter(
      (customer) => customer._id! !== customerId,
    );
    onCustomerIdsChange(newCustomerIds);
    setSelectedCustomers(newSelectedCustomers);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Customer Scope</CardTitle>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Define which customers this rule applies to
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Scope Type</Label>
            <Select value={scopeType} onValueChange={onScopeTypeChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="global">Global (all customers)</SelectItem>
                <SelectItem value="classification">Customer Classification</SelectItem>
                <SelectItem value="customers">Specific Customers</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {scopeType === 'classification' && (
            <div>
              <Label>Customer Classification</Label>
              <Select
                value={formData.scope?.customerClassification || ''}
                onValueChange={(value) =>
                  onFormDataChange({
                    scope: {
                      ...formData.scope,
                      customerClassification: value as CustomerClassificationEnum,
                    },
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select classification" />
                </SelectTrigger>
                <SelectContent>
                  {customerClassifications.map((classification) => (
                    <SelectItem key={classification.value} value={classification.value}>
                      {classification.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {scopeType === 'customers' && (
            <div>
              <Label>Customers</Label>
              <AutocompleteUser setCustomer={handleCustomerSelect} className="mt-1" limit={3} />
              {selectedCustomers.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-sm text-slate-500 dark:text-slate-400">Selected customers:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedCustomers.map((customer) => {
                      const customerId = customer._id!;
                      const customerName =
                        `${customer.name?.first || ''} ${customer.name?.last || ''}`.trim() ||
                        'Unknown';
                      return (
                        <Badge
                          key={customerId}
                          variant="outline"
                          className="flex items-center gap-1"
                        >
                          <span className="truncate max-w-32">{customerName}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-4 w-4 p-0 hover:bg-transparent"
                            onClick={() => handleRemoveCustomer(customerId)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {scopeType === 'global' && (
            <div className="p-4 bg-slate-100 rounded-lg dark:bg-slate-800">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                This rule will apply to all customers regardless of classification or specific
                assignment.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Location Targeting</CardTitle>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Specify which locations this rule applies to
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Locations (Optional)</Label>
            <LocationSelector
              selectedItems={selectedLocations}
              onSelectionChange={onSelectedLocationsChange}
            />
            <p className="text-sm text-slate-500 mt-1 dark:text-slate-400">
              Leave empty to apply to all locations, or select specific zip codes or cities to limit
              the rule's scope. Selecting a city will include all zip codes in that city.
            </p>
          </div>

          {selectedLocations.length === 0 && (
            <div className="p-4 bg-slate-100 rounded-lg dark:bg-slate-800">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                This rule will apply to all locations. Select specific zip codes or cities above to
                limit the geographic scope.
              </p>
            </div>
          )}

          {selectedLocations.length > 0 && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg dark:border-slate-800">
              <p className="text-sm text-blue-800">
                This rule will apply to the selected locations:{' '}
                {selectedLocations
                  .map((item) => item.display)
                  .slice(0, 3)
                  .join(', ')}
                {selectedLocations.length > 3 && ` and ${selectedLocations.length - 3} more`}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Effective Period</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="effectiveFrom">Effective From</Label>
              <DatePicker
                date={formData.effectiveFrom ? parseISO(formData.effectiveFrom) : undefined}
                setDate={(date) => {
                  const formattedDate = date ? format(date, 'yyyy-MM-dd') : '';
                  onFormDataChange({
                    effectiveFrom: formattedDate,
                  });
                }}
              />
              {errors.effectiveFrom && (
                <p className="text-sm text-destructive mt-1 dark:text-destructive">
                  {errors.effectiveFrom}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="effectiveTo">Effective To (Optional)</Label>
              <DatePicker
                date={formData.effectiveTo ? parseISO(formData.effectiveTo) : undefined}
                setDate={(date) =>
                  onFormDataChange({
                    effectiveTo: date ? format(date, 'yyyy-MM-dd') : undefined,
                  })
                }
                allowClear={true}
              />
              <p className="text-sm text-slate-500 mt-1 dark:text-slate-400">
                Leave empty for indefinite rules that remain active until manually deactivated.
              </p>
              {errors.effectiveTo && (
                <p className="text-sm text-destructive mt-1 dark:text-destructive">
                  {errors.effectiveTo}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
